import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import {
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatProgressBar } from '@angular/material/progress-bar'
import {
  AddressSchema,
  amountMask,
  ApiService,
  AppointmentModel,
  AppService,
  Card,
  Clinic,
  ClinicService,
  ConfirmService,
  CreatePaymentIntentPayload,
  CustomFieldService,
  emailValidator,
  InvoiceModel,
  InvoicePlan,
  Patient, PatientDataService, PatientPalRestError,
  PAYMENT_PROVIDER,
  PaymentResponse,
  PaymentService,
  ProgressIndicatorService, RestResponse,
  TransactionMethod,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { PaymentMethodCreateParams } from '@stripe/stripe-js'
import { first, range } from 'lodash'
import moment from 'moment'
import { from, Observable, of, Subject, switchMap } from 'rxjs'
import { catchError, combineLatestWith, filter, map, takeUntil, tap } from 'rxjs/operators'
import { v4 as uuidv4 } from 'uuid'

import { environment } from '../../../../../environments/environment'
import {
  PaymentCardFormComponent,
  PaymentCardFormConfiguration,
  PaymentProviderConfiguration,
  TilledBillingDetail,
} from '../../payment-card-form'

@Component({
  selector: 'mmx-charge-card',
  templateUrl: './charge-card.component.html',
  styleUrls: ['./charge-card.component.scss'],
  standalone: false,
})
export class ChargeCardComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  @ViewChild(PaymentCardFormComponent) paymentCardFormComponent: PaymentCardFormComponent
  @Output() newCharge = new EventEmitter<any>()
  @Input() patient: Patient
  @Input() appointment: AppointmentModel
  @Input() chargeAmount: number
  @Input() invoice: InvoiceModel
  @Input() compact = false

  clinic: Clinic

  get card(): Card | undefined {
    return this.creditCards.find(card => card.id === this.chargeForm.value.creditCard)
  }

  chargeForm = this.fb.group({
    creditCard: this.fb.control<string>(null, Validators.required),
    checkNumber: this.fb.control<string>(null),
    amount: this.fb.control<number>(null, [Validators.required, Validators.min(0.01)]),
    paymentMethod: this.fb.control<'creditCard' | 'check' | 'cash'>('creditCard', Validators.required),
    captureTime: this.fb.control('eod'),
    areNotesVisibleToPatient: this.fb.control<boolean>(false),
    notes: this.fb.control<string>(null),
    isDiscount: this.fb.control<boolean>(false),
    customFields: this.fb.control<Record<string, any>>(null),
    holderName: this.fb.control<string>(null),
    holderAddress: this.fb.control<AddressSchema>(null),
    email: this.fb.control<string>(null), // used for Tilled only
    // `emailValidator` (not Angular's `Validators.email`) so the client enforces exactly
    // what the API enforces - see libs/shared/.../email.validator.ts.
    patientEmail: this.fb.control<string>(null, [emailValidator]), // used to set patient email if not set
  })

  amountMask = amountMask()
  serverResponse: string
  responseColor: string
  isSaving = false

  /**
   * True once the card has been captured but the payment could not be recorded. Submission
   * stays locked in this state so the same card is never captured twice for one invoice.
   */
  chargeCaptured = false

  /**
   * Identifies a single payment attempt. Sent to the API to derive a Stripe idempotency key
   * so that repeated submissions reuse one PaymentIntent instead of creating another charge.
   */
  private attemptToken: string = uuidv4()
  months = range(2, 37)
  feePercentage: number
  feeAmount: number
  isDiscount = false
  invoicePlan: InvoicePlan = { }
  hold = false

  paymentProviderConfiguration: PaymentProviderConfiguration
  cardFormConfiguration: PaymentCardFormConfiguration

  private onDestroy = new Subject<void>()

  private creditCardValidatorFn: ValidatorFn = (): ValidationErrors | null => {
    if (this.chargeForm.get('creditCard').value === 'new' && this.paymentCardFormComponent && !this.paymentCardFormComponent.valid) {
      return { paymentCardFormInvalid: true }
    }

    return null
  }

  get showPaymentPlan() {
    const { plan } = this.invoice
    const { modulesPreferences } = this.clinic

    if (plan) {
      return {
        available: true,
      }
    }

    if (!modulesPreferences?.paymentPlans) {
      return {
        available: false,
        reason: 'disabled',
      }
    }

    const { minLoanAmount } = modulesPreferences

    if (minLoanAmount && minLoanAmount >= this.invoice.total) {
      return {
        available: false,
        reason: 'amount-too-low',
      }
    }

    return {
      available: true,
    }
  }

  get total() {
    if (this.chargeForm.get('isDiscount').value && this.invoice.amountPaid === 0) {
      return this.chargeAmount - this.discountPayment
    }

    return this.chargeAmount
  }

  get discountPayment() {
    return Number((this.clinic.modulesPreferences.promptPaymentDiscount * this.invoice.total / 100).toFixed())
  }

  creditCards: Card[] = []

  cards$ = this.patientData.cards$.pipe(
    takeUntil(this.onDestroy),
    tap(cards => {
      this.creditCards = cards
      this.chargeForm.get('creditCard').enable()
      this.chargeForm.updateValueAndValidity()

      if (cards && cards.length) {
        this.chargeForm.get('creditCard').setValue(first(cards).id)
        this.chargeForm.updateValueAndValidity()
      }
    }),
  )

  constructor(
    private dialog: MatDialog,
    private progressService: ProgressIndicatorService,
    private clinicService: ClinicService,
    private restService: ApiService,
    private patientData: PatientDataService,
    private confirmService: ConfirmService,
    private translateService: TranslateService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private paymentService: PaymentService,
    private fb: FormBuilder,
    public customFields: CustomFieldService,
  ) {}

  ngOnInit() {
    this.clinic = this.clinicService.clinic

    if (this.clinic.settings.defaultCurrency) {
      this.amountMask = amountMask(this.clinic.settings.defaultCurrency)
    }
    if (this.clinic.modulesPreferences.maxLoanTerm) {
      this.months.splice(this.clinic.modulesPreferences.maxLoanTerm - 1, 0, null)
    }

    this.chargeForm.patchValue({
      amount: this.chargeAmount,
      isDiscount: !!this.invoice.discount,
    })

    if (this.invoice.plan) {
      this.feePercentage = this.invoice.plan.feePercentage
      this.feeAmount = this.invoice.plan.feeAmount
    } else {
      this.feePercentage = this.clinic.modulesPreferences.feePercentage ?? 0
      this.feeAmount = this.clinic.modulesPreferences.feeAmount ?? 0
    }

    this.chargeForm.controls.creditCard.valueChanges.pipe(
      takeUntil(this.onDestroy),
      combineLatestWith(this.cards$.pipe(takeUntil(this.onDestroy))),
      map(([cardId, cards]) => cards.find(card => card.id === cardId)),
      tap(card => this.creditCardChanged(card)),
    ).subscribe()

    this.cards$.subscribe()

    this.paymentProviderConfiguration = {
      provider: this.clinic.paymentProvider.selectedProvider,
      locale: this.appService.currentLanguage,
      stripeAccount: this.clinic.paymentProvider?.id || '',
      stripePublicKey: environment.stripePublicKey,
      tilledId: this.clinic.paymentProvider.tilledId,
      tilledPublicKey: environment.tilledPublicKey,
      tilledProductionMode: environment.tilledProductionMode,
    }

    this.cardFormConfiguration = {
      forcedSave: false,
      allowSave: !!this.patient,
    }

    if (this.patient) {
      this.chargeForm.patchValue({
        holderName: this.patient.standardName.toUpperCase(),
        email: this.patient.email,
      })
    }
  }

  ngAfterViewChecked() {
    if (this.paymentCardFormComponent) {
      // checking for validator in order to add/remove everything once
      if (!this.chargeForm.hasValidator(this.creditCardValidatorFn)) {
        this.chargeForm.controls.holderName.addValidators(Validators.required)
        if (this.clinic.paymentProvider.selectedProvider === PAYMENT_PROVIDER.TILLED) {
          this.chargeForm.controls.holderAddress.addValidators(Validators.required)
          this.chargeForm.controls.email.addValidators(Validators.required)
        }

        this.chargeForm.addValidators(this.creditCardValidatorFn)
        this.chargeForm.updateValueAndValidity()
        this.cdr.detectChanges()
      }
    } else if (this.chargeForm.hasValidator(this.creditCardValidatorFn)) {
      if (this.clinic.paymentProvider.selectedProvider === PAYMENT_PROVIDER.TILLED) {
        this.chargeForm.removeControl('holderAddress')
        this.chargeForm.removeControl('email')
      }
      this.chargeForm.removeValidators(this.creditCardValidatorFn)
      this.chargeForm.updateValueAndValidity()
      this.cdr.detectChanges()
    }
  }

  ngOnDestroy() {
    this.onDestroy.next()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.chargeForm && changes['chargeAmount'] && (
      changes['chargeAmount'].isFirstChange() || changes['chargeAmount'].previousValue !== changes['chargeAmount'].currentValue)
    ) {
      this.chargeForm.get('amount').setValue(changes['chargeAmount'].currentValue)
    }
  }

  creditCardChanged(creditCardValue: Card) {
    this.updateHoldState(creditCardValue?.provider)
  }

  public setPaymentMethod(): void {
    if (this.chargeForm.value.paymentMethod === 'creditCard') {
      this.chargeForm.get('checkNumber').setValidators(null)
      this.chargeForm.get('checkNumber').updateValueAndValidity()

      if (this.creditCards.length > 0) {
        this.chargeForm.get('creditCard').setValue(first(this.creditCards).id)
      }

      this.chargeForm.get('creditCard').setValidators([Validators.required])
      this.chargeForm.get('creditCard').updateValueAndValidity()

      this.chargeForm.get('checkNumber').setValue(null)
    } else if (this.chargeForm.value.paymentMethod === 'check') {
      this.chargeForm.get('creditCard').setValidators(null)
      this.chargeForm.get('creditCard').setValue(null)
      this.chargeForm.get('creditCard').updateValueAndValidity()

      // this.chargeForm.get('accountNumber').setValidators(Validators.required)
      // this.chargeForm.get('routingNumber').setValidators([
      //   Validators.required,
      //   Validators.minLength(9),
      //   Validators.maxLength(9),
      // ])

      this.chargeForm.get('checkNumber').setValidators(Validators.required)
      this.chargeForm.get('checkNumber').updateValueAndValidity()
    } else if (this.chargeForm.value.paymentMethod === 'cash') {
      this.chargeForm.get('checkNumber').setValidators(null)
      this.chargeForm.get('checkNumber').setValue(null)
      this.chargeForm.get('checkNumber').updateValueAndValidity()

      this.chargeForm.get('creditCard').setValidators(null)
      this.chargeForm.get('creditCard').setValue(null)
      this.chargeForm.get('creditCard').updateValueAndValidity()
    }
  }

  public validateCharge(holdPayment: boolean) {
    if (this.chargeForm.invalid) {
      return
    }

    // Show alert if amount remaining is less or equal to 0
    if (this.invoice.amountRemaining <= 0) {
      this.confirmService.confirm({
        message: `Are you sure to trigger this payment?<br>Full payment has been received.`,
        html: true,
      }).pipe(
        filter(Boolean),
        switchMap(() => this.saveCard(holdPayment)),
        tap(() => this.submitCharge(holdPayment)),
      ).subscribe()
    } else {
      this.saveCard(holdPayment).pipe(tap(() => this.submitCharge(holdPayment))).subscribe()
    }
  }

  public changeDiscount(): void {
    // Update invoice with discount
    let message = 'removed'
    let isDiscount = false
    const paymentOptions: any = {
      paymentOption: this.chargeForm.get('paymentMethod').value,
    }

    if (this.chargeForm.get('isDiscount').value) {
      message = 'applied'
      isDiscount = true
      paymentOptions.paymentDiscount = {
        percent: this.clinic.modulesPreferences.promptPaymentDiscount,
        amount: this.discountPayment,
        total: this.invoice.total - this.discountPayment,
      }
    }

    const route = `/invoices/${this.invoice.id}/pay-options`
    this.restService.post(route, paymentOptions).subscribe(
      () => {
        this.progressService.snack(`Discount ${message}`)
      },
      () => {
        this.chargeForm.get('isDiscount').setValue(!isDiscount)
        this.progressService.snack('Error saving discount')
      },
    )
  }

  private paymentOrIntentId: string

  private saveCard(holdPayment: boolean) {
    if (this.chargeForm.value.creditCard === 'new') {
      this.isSaving = true
      this.progressBar.mode = 'indeterminate'
      const amount = this.safeCurrency(this.chargeForm.value.amount)
      return this.createNewCardPayment(amount, holdPayment).pipe(
        tap(paymentOrIntentId => {
          this.paymentOrIntentId = paymentOrIntentId
          this.chargeForm.patchValue({ creditCard: paymentOrIntentId })
        }),
      )
    } else {
      return of(null)
    }
  }

  private submitCharge(holdPayment: boolean) {
    this.isSaving = true
    this.progressBar.mode = 'indeterminate'
    const progress = this.progressService.start()
    const patientId = this.patient.id
    const amount = this.safeCurrency(this.chargeForm.value.amount)
    const notes = (this.chargeForm.value.notes || '').trim()
    const patientEmail = this.chargeForm.value.patientEmail
    const customFields = this.chargeForm.value.customFields
    const areNotesVisibleToPatient = this.chargeForm.value.areNotesVisibleToPatient

    let obs: Observable<RestResponse<PaymentResponse>>

    if (this.invoice && this.chargeForm.value.paymentMethod === 'creditCard') {
      const { captureTime } = this.chargeForm.value
      let captureAt: moment.Moment | undefined

      if (holdPayment && captureTime === 'eod') {
        if (!this.invoice.pastDue && this.invoice.dueDate && this.invoice.dueDays < 7) {
          captureAt = this.invoice.dueDate.clone().set({ hour: 23, minute: 45, second: 0 })
        } else {
          captureAt = moment().set({ hour: 23, minute: 45, second: 0 })
        }
      }

      const pay: any = {
        amount,
        notes,
        areNotesVisibleToPatient,
        holdPayment,
        holderName: this.card?.name || this.chargeForm.value.holderName,
        captureAt: captureAt ? captureAt.toISOString() : null,
        method: TransactionMethod.STAFF_CREDIT_CARD,
        paymentMethodTypes: ['card'],
        patientEmail,
        customFields,
      }

      let provider: PAYMENT_PROVIDER

      if (this.card) {
        if (this.card.provider === 'stripe') {
          provider = PAYMENT_PROVIDER.STRIPE
        } else {
          provider = PAYMENT_PROVIDER.TILLED
        }
      } else {
        provider = this.clinic.paymentProvider.selectedProvider
      }

      if (this.paymentOrIntentId) {
        if (provider === PAYMENT_PROVIDER.STRIPE) {
          pay.paymentIntentId = this.paymentOrIntentId
        } else {
          pay.paymentMethodId = this.paymentOrIntentId
        }
      } else {
        pay.paymentMethodId = this.chargeForm.value.creditCard
      }

      pay.provider = provider

      obs = this.restService.submitInvoicePayment(
        this.invoice.id,
        pay,
      )
    } else {
      const paymentData: any = {
        invoiceId: this.invoice.id,
        patientEmail,
        chargeDetails: {
          paymentMethodId: null,
          checkNumber: this.chargeForm.value.checkNumber,
          amount,
          paymentMethod: this.chargeForm.value.paymentMethod,
          notes,
          areNotesVisibleToPatient,
          customFields,
        },
      }

      obs = this.restService.processPayment(patientId, paymentData)
    }

    obs.subscribe({
      next: (res: any) => {
        this.serverResponse = 'transactions.transaction-successful'
        this.responseColor = `successCls`
        this.chargeForm.reset()

        // The dialog stays open for further charges, so start a clean attempt.
        this.chargeCaptured = false
        this.attemptToken = uuidv4()

        this.chargeForm.patchValue({ paymentMethod: 'creditCard' })
        this.chargeForm.patchValue({ isDiscount: this.invoice.discount ? true : false })
        this.chargeForm.patchValue({ captureTime: 'eod' })

        this.setPaymentMethod()

        if (res != null && res.data != null && res.data.invoice != null) {
          this.invoice = new InvoiceModel(res.data.invoice)
        }

        // Emit new transaction
        this.newCharge.emit(res.data)

        this.isSaving = false
        this.progressBar.mode = 'determinate'
        progress.stop(this.translateService.instant(this.serverResponse))
      },
      error: (err?: PatientPalRestError) => {
        this.isSaving = false
        this.responseColor = `errorCls`
        // When the card was already captured, only the follow-up bookkeeping failed. Report
        // the payment as taken and keep submission locked so it cannot be captured twice;
        // the server reconciles the record from the payment provider's webhook.
        const errorMessage = this.chargeCaptured
          ? 'transactions.charged-receipt-failed'
          : (err?.error?.message || 'transactions.unable-process-payment')
        this.serverResponse = errorMessage
        this.progressBar.mode = 'determinate'
        progress.stop(this.translateService.instant(errorMessage))
      },
    })
  }

  public getErrorMessage(controlName: string, actualName: string): string {
    const control = this.chargeForm.get(controlName)
    if (control) {
      if (control.hasError('required')) {
        return `${actualName} is required`
      } else if (control.hasError('minlength') || control.hasError('maxlength')) {
        return `${actualName} is an invalid length`
      } else if (control.hasError('min')) {
        const error = control.getError('min')
        return `${actualName} must be at least ${error.min}`
      } else if (control.hasError('pattern')) {
        return `${actualName} must be a number`
      }
    }

    return ''
  }

  // Set the patient responsibility (amount patient owes) from the estimate to the payment
  public setPatientAmount(amount: number): void {
    this.chargeForm.get('amount').setValue(amount)
  }

  public safeCurrency(amount: number | string): number | undefined {
    if (typeof amount === 'number') {
      return amount
    } else if (typeof amount === 'string') {
      return parseFloat(amount.replace(/[^0-9.-]+/g, ''))
    }
  }

  paymentFormUpdated() {
    this.chargeForm.updateValueAndValidity()
    if (this.paymentCardFormComponent?.valid){
      this.updateHoldState(this.paymentCardFormComponent.provider)
    }
  }

  private createNewCardPayment(amount: number, holdPayment: boolean): Observable<string | null> {
    if (this.clinic.paymentProvider.selectedProvider === PAYMENT_PROVIDER.STRIPE) {
      const payload: CreatePaymentIntentPayload = {
        amount,
        saveCard: this.paymentCardFormComponent.shouldSaveCard || false,
        invoiceId: this.invoice.id,
        patientId: this.patient.id,
        holdPayment: holdPayment,
        // Surfaced here so the API rejects a bad address BEFORE the card is charged.
        patientEmail: this.chargeForm.value.patientEmail,
        // Stable across retries of this attempt -> Stripe idempotency key -> no dupes.
        attemptToken: this.attemptToken,
      }

      const billingDetails: PaymentMethodCreateParams.BillingDetails = {
        name: this.chargeForm.value.holderName,
      }

      const holderAddress = this.chargeForm.value.holderAddress
      if (holderAddress) {
        billingDetails.address = {
          country: holderAddress.country,
          state: holderAddress.state,
          city: holderAddress.city,
          line1: holderAddress.address1,
          postal_code: holderAddress.zip,
        }
      }

      return this.paymentService.createPaymentIntent(payload).pipe(
        switchMap((paymentIntent) => this.paymentCardFormComponent.confirmStripePayment(paymentIntent, billingDetails)),
        map(result => {
          if (result.error) {
            throw result.error
          }
          // Past this point the card has been captured. Any later failure must not be
          // presented as a failed payment, and must not re-arm submission.
          this.chargeCaptured = true
          return result.paymentIntent.id
        }),
        catchError((err) => {
          const error = err?.error?.message || 'transactions.unable-process-payment'
          this.progressService.snack(this.translateService.instant(error))
          this.progressBar.mode = 'determinate'
          return of(null)
        }),
      )
    } else {
      const holderAddress = this.chargeForm.value.holderAddress
      const billingDetails: TilledBillingDetail = {
        name: this.chargeForm.value.holderName,
        email: this.chargeForm.value.email,
        address: {
          country: holderAddress.country,
          zip: holderAddress.zip,
          state: holderAddress.state,
          city: holderAddress.city,
          street: holderAddress.address1,
        },
      }

      return from(this.paymentCardFormComponent.createTilledPaymentMethod(billingDetails)).pipe(
        switchMap(({ id }) => {
          if (this.paymentCardFormComponent.shouldSaveCard) {
            return this.paymentService.attachTilledPaymentMethodToCustomer(id, this.patient.id).pipe(
              map(result => result.id),
            )
          } else {
            return of(id)
          }
        }),
      )
    }
  }

  private updateHoldState(provider: string) {
    this.hold = provider === PAYMENT_PROVIDER.STRIPE && this.clinic.modulesPreferences.paymentOnHold && this.clinic.modules.billing
    && !this.invoice.pastDue && this.invoice.dueDays < 7
  }
}
